/**
 * The comment thread's live-update stream: a ticketed EventSource on
 * /api/issues/:id/comments/stream with the client-owned reconnect ladder.
 * Extracted VERBATIM from CommentThread.tsx (refactor phase C1,
 * docs/refactoring/plans/2026-09-08-commentthread.md); the effect body,
 * its constants, its comments and its deps are unchanged, and the log
 * prefix stays "[CommentThread]" because those strings are what production
 * consoles and the PostHog console-error autocapture already key on.
 *
 * Behaviour is pinned by __tests__/CommentThread.stream.test.tsx (fake
 * fetch + fake EventSource + fake timers, driven through the component) and
 * by the two source guards, CommentThread.connectionCap.source.test.ts and
 * CommentThread.sseError.test.tsx, which now read THIS file.
 *
 * useNotifications.ts carries a twin of this ladder for the bell; folding
 * the two onto one hook is a separate change (see the plan).
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isConnectionCapError, SSE_CONNECTION_CAP_COOLDOWN_MS } from '@overlayqa/shared';
import { useAuth } from '../../lib/auth';
import { apiBaseUrl } from '../../lib/apiBaseUrl';
import { log } from '@/lib/logger';
import { createSseDropTracker } from '@/lib/sseDropTracker';
import { capture } from '@/lib/analytics';
import {
  SSE_STREAM_FAILURE_EVENT,
  buildSseStreamFailureProps,
  isEscalationCrossing,
} from '@/lib/sseStreamFailure';

export function useCommentStream(issueId: string): void {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  // SSE connection — refetch on incoming events to leverage TanStack Query cache.
  //
  // Auth is ticket-based, not token-based: EventSource cannot set custom
  // headers, and the old approach of putting a Clerk JWT in the query
  // string leaked the token to logs, history, and referrer headers — AND
  // was functionally broken, because the server's old code verified the
  // query param as an extension session JWT (HMAC-SHA256), which a Clerk
  // JWT never matches. This connection silently 401'd on every page load.
  //
  // New flow: POST /stream-ticket with the Bearer token to get a 60-second
  // single-use ticket, then open EventSource with ?ticket=<value>.
  useEffect(() => {
    let es: EventSource | null = null;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // Exponential backoff for reconnects. Without this, the thread leaned on the
    // browser's native EventSource auto-reconnect AND retried the ticket POST
    // with no delay — so a rate-limited / flapping stream could hammer the
    // server (POST /comments/stream-ticket + GET /comments via cache
    // invalidation), all keyed by the user's id under the global 100/min limit.
    const RECONNECT_BASE_MS = 2000;
    const RECONNECT_MAX_MS = 30000;
    let reconnectDelay = RECONNECT_BASE_MS;
    // Escalates repeated consecutive drops to error; reset on the server's
    // `connected` event (only sent after a successful subscribe), NOT onopen.
    const dropTracker = createSseDropTracker();
    // One error-level log per capped streak: the condition is expected (the user
    // has too many streams open) and self-healing, so reporting every retry
    // would restore the noise this fix removed. First hit per streak = one
    // warn + one sse_stream_failed capture.
    let capLogged = false;

    /**
     * @param fixedDelayMs When set, reconnect after exactly this delay and HOLD
     * it (no doubling) until a `connected` event confirms a real subscription.
     * Used for the connection-cap cooldown, which must not tighten on repeated
     * rejections.
     */
    function scheduleReconnect(fixedDelayMs?: number) {
      if (cancelled || reconnectTimer) return;
      const delay = fixedDelayMs ?? reconnectDelay;
      reconnectDelay = fixedDelayMs ?? Math.min(delay * 2, RECONNECT_MAX_MS);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    }

    async function connect() {
      if (cancelled) return;
      const token = await getToken();
      if (!token || cancelled) return;

      const apiUrl = apiBaseUrl();

      let ticket: string;
      try {
        const ticketResponse = await fetch(
          `${apiUrl}/api/issues/${issueId}/comments/stream-ticket`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (!ticketResponse.ok) {
          // Same self-healing class as the thrown-fetch catch below: a 401 is
          // a Clerk token mid-refresh, a 429 gets the jump-to-max just under
          // this. Drop tracker like every sibling: warn below threshold,
          // error only when repeatedly failing. (Reverses the 2026-07-10
          // "responded non-200 is a genuine signal" note: a single 401 filed
          // a fake $exception 2026-07-21, a 429 pair on 2026-07-05.)
          if (dropTracker.recordDrop() === 'error') {
            // warn + once-per-streak capture, NOT error: the escalation
            // itself minted fake $exceptions (decision 2026-08-04).
            const drops = dropTracker.consecutiveDrops();
            log.warn('[CommentThread] SSE ticket failing repeatedly', {
              status: ticketResponse.status,
              consecutiveDrops: drops,
            });
            if (isEscalationCrossing(drops)) {
              capture(
                SSE_STREAM_FAILURE_EVENT,
                buildSseStreamFailureProps('comments', 'ticket_failing', {
                  consecutiveDrops: drops,
                  status: ticketResponse.status,
                }),
              );
            }
          } else {
            log.warn(
              '[CommentThread] Failed to obtain SSE ticket',
              { status: ticketResponse.status },
            );
          }
          // A 429 means we are rate-limited: jump straight to the max delay so
          // we stop adding to the storm instead of retrying immediately.
          if (ticketResponse.status === 429) reconnectDelay = RECONNECT_MAX_MS;
          scheduleReconnect();
          return;
        }
        const body = (await ticketResponse.json()) as { data?: { ticket?: string } };
        if (typeof body.data?.ticket !== 'string') {
          log.error('[CommentThread] SSE ticket response missing ticket field');
          scheduleReconnect();
          return;
        }
        ticket = body.data.ticket;
      } catch (err) {
        // A thrown fetch (server unreachable) is a network blip in the SAME
        // self-healing class as an es.onerror drop: scheduleReconnect recovers
        // it and storms the reconnect ladder, so an unconditional error would
        // mint a $exception per retry (incident 2026-07-10). Route it through
        // the drop tracker like onerror and like the HTTP `!ok` path above
        // (drop-tracked since 2026-07-21).
        if (dropTracker.recordDrop() === 'error') {
          // warn + once-per-streak capture, NOT error: the escalation itself
          // minted fake $exceptions (decision 2026-08-04).
          const drops = dropTracker.consecutiveDrops();
          log.warn('[CommentThread] SSE ticket fetch failing repeatedly', {
            consecutiveDrops: drops,
            error: err instanceof Error ? err.message : String(err),
          });
          if (isEscalationCrossing(drops)) {
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('comments', 'connect_failing', {
                consecutiveDrops: drops,
                error: err,
              }),
            );
          }
        } else {
          log.warn('[CommentThread] SSE ticket fetch failed, scheduling reconnect');
        }
        scheduleReconnect();
        return;
      }

      if (cancelled) return;

      const url = `${apiUrl}/api/issues/${issueId}/comments/stream?ticket=${encodeURIComponent(ticket)}`;
      es = new EventSource(url);

      const handler = () => {
        queryClient.invalidateQueries({ queryKey: ['issue-comments', issueId] });
      };

      es.addEventListener('comment_created', handler);
      es.addEventListener('comment_updated', handler);
      es.addEventListener('comment_deleted', handler);
      es.addEventListener('connected', () => {
        // routes/issueCommentSSE.ts:138 sends `connected` after subscribing, but does
        // not replay events missed while offline. Refresh after a failed
        // connection so the thread catches up without requiring a page reload.
        const needsCatchUp = capLogged || dropTracker.consecutiveDrops() > 0;
        // Reset backoff only on a CONFIRMED subscription. NOT es.onopen, which
        // fires as soon as the 200 headers land, including on the server's
        // TOO_MANY_CONNECTIONS path, which 200s then closes without `connected`.
        // Resetting there discarded the backoff on exactly the failure it
        // exists for (incident 2026-07-05).
        dropTracker.recordConnected();
        reconnectDelay = RECONNECT_BASE_MS;
        capLogged = false;
        if (needsCatchUp) handler();
      });
      es.onerror = (event) => {
        // The server rejects past its per-user / per-issue connection cap
        // (commentBroadcast.ts) by responding 200, emitting this `error` event
        // with a code, then closing without `connected`. Retrying on the normal
        // cadence just burns the per-user SSE rate-limit bucket; the cap clears
        // on its own once another stream closes.
        if (isConnectionCapError(event)) {
          if (capLogged) {
            log.warn('[CommentThread] SSE still at the connection limit, waiting out the cooldown');
          } else {
            capLogged = true;
            // warn + queryable capture, NOT error: the cap is a handled,
            // self-recovering condition (since 2026-08-04 the server evicts
            // the user's oldest stream rather than refusing the newest), and
            // log.error filed it as a fake $exception (row 2026-08-01).
            log.warn('[CommentThread] SSE connection limit reached', {
              cooldownMs: SSE_CONNECTION_CAP_COOLDOWN_MS,
            });
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('comments', 'connection_cap'),
            );
          }
          es?.close();
          es = null;
          scheduleReconnect(SSE_CONNECTION_CAP_COOLDOWN_MS);
          return;
        }
        // warn, NOT error: the browser fires onerror on EVERY disconnect, and
        // we take over reconnection below — so a disconnect is expected and
        // self-healing. log.error → console.error → PostHog console-error
        // autocapture would surface each routine ~15-min forced reconnect as a
        // false production $exception (observed 2026-07-03, agenciaeplus team;
        // an upstream max-connection-duration drops the stream on a cadence and
        // each reconnect succeeds). A stream that repeatedly never reaches a
        // subscribed state (no `connected` between drops) escalates to a warn
        // with the count + one queryable sse_stream_failed capture per streak
        // (the error-level escalation itself minted fake $exceptions, decision
        // 2026-08-04).
        if (dropTracker.recordDrop() === 'error') {
          const drops = dropTracker.consecutiveDrops();
          log.warn('[CommentThread] SSE connection failing repeatedly', {
            consecutiveDrops: drops,
          });
          if (isEscalationCrossing(drops)) {
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('comments', 'stream_failing', {
                consecutiveDrops: drops,
              }),
            );
          }
        } else {
          log.warn('[CommentThread] SSE error');
        }
        // Take over reconnection: close to stop the browser's instant native
        // auto-reconnect, then reconnect on our exponential backoff schedule.
        es?.close();
        es = null;
        scheduleReconnect();
      };
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [issueId, queryClient, getToken]);
}
